/**
 * Error Handling Utilities
 * Provides user-friendly error messages and error logging
 */

import { ErrorCode } from '../../types/auth.types';
import type { ApiError } from '../../types/auth.types';

/**
 * User-friendly error messages mapped to error codes
 */
export const ERROR_MESSAGES: Record<ErrorCode, string> = {
  AUTH_TOKEN_EXPIRED: 'Your session has expired. Please log in again.',
  AUTH_TOKEN_INVALID: 'Invalid authentication. Please log in again.',
  AUTH_UNAUTHORIZED: 'You do not have permission to access this resource.',
  AUTH_REFRESH_FAILED: 'Failed to refresh your session. Please log in again.',
  PAYMENT_FAILED: 'Payment failed. Please check your payment method and try again.',
  PAYMENT_METHOD_INVALID: 'Invalid payment method. Please update your payment information.',
  SUBSCRIPTION_NOT_FOUND: 'Subscription not found. Please contact support.',
  PROMO_CODE_INVALID: 'Invalid promotional code.',
  PROMO_CODE_EXPIRED: 'This promotional code has expired.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  SERVER_ERROR: 'Server error. Please try again later.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.',
};

/**
 * Get user-friendly error message from error code
 */
export function getErrorMessage(code: ErrorCode): string {
  return ERROR_MESSAGES[code] || ERROR_MESSAGES.UNKNOWN_ERROR;
}

/**
 * Get user-friendly error message from ApiError or unknown error
 */
export function getApiErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    // Use the error's message if it's user-friendly, otherwise use the mapped message
    if (error.message && !error.message.includes('Error:') && error.message.length < 200) {
      return error.message;
    }
    return getErrorMessage(error.code);
  }
  
  // Handle non-ApiError types
  if (error instanceof Error) {
    return error.message;
  }
  
  return 'An unexpected error occurred. Please try again.';
}

/**
 * Error logging levels
 */
export const ErrorLevel = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  CRITICAL: 'critical',
} as const;

export type ErrorLevel = typeof ErrorLevel[keyof typeof ErrorLevel];

/**
 * Error log entry
 */
interface ErrorLogEntry {
  errorId: string; // Unique error ID for support reference
  timestamp: string;
  level: ErrorLevel;
  code?: ErrorCode;
  message: string;
  details?: Record<string, any>;
  stack?: string;
  userId?: string;
  url?: string;
  userAgent?: string;
  step?: string; // Registration step if applicable
  context?: string; // Additional context
}

/**
 * Generate unique error ID for support reference
 */
function generateErrorId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 9);
  return `ERR-${timestamp}-${random}`.toUpperCase();
}

/**
 * Log error to console and external service (if configured)
 */
export function logError(
  error: ApiError | Error | unknown,
  level: ErrorLevel = ErrorLevel.ERROR,
  context?: Record<string, any>
): string {
  const errorId = generateErrorId();
  
  const logEntry: ErrorLogEntry = {
    errorId,
    timestamp: new Date().toISOString(),
    level,
    message: '',
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    step: context?.step,
    context: context?.context,
  };

  // Extract error information
  if (isApiError(error)) {
    logEntry.code = error.code;
    logEntry.message = error.message;
    logEntry.details = { ...error.details, ...context, statusCode: error.statusCode };
  } else if (error instanceof Error) {
    logEntry.message = error.message;
    logEntry.stack = error.stack;
    logEntry.details = context;
  } else {
    logEntry.message = String(error);
    logEntry.details = context;
  }

  // Log to console based on level
  const consoleMethod = level === ErrorLevel.CRITICAL || level === ErrorLevel.ERROR ? 'error' : 'warn';
  console[consoleMethod]('[Error Log]', logEntry);

  // Store in session storage for support reference
  try {
    const recentErrors = getRecentErrors();
    recentErrors.push({
      errorId,
      timestamp: logEntry.timestamp,
      level,
      message: logEntry.message,
      code: logEntry.code,
    });
    
    // Keep only last 10 errors
    const trimmedErrors = recentErrors.slice(-10);
    sessionStorage.setItem('plz-error-log', JSON.stringify(trimmedErrors));
  } catch (e) {
    // Ignore storage errors
  }

  // TODO: Send to external error tracking service (e.g., Sentry)
  // if (import.meta.env.VITE_SENTRY_DSN) {
  //   Sentry.captureException(error, { 
  //     level, 
  //     extra: logEntry.details,
  //     tags: { errorId, step: context?.step }
  //   });
  // }

  return errorId;
}

/**
 * Type guard to check if error is ApiError
 */
function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    'statusCode' in error
  );
}

/**
 * Log network error
 */
export function logNetworkError(error: unknown, endpoint?: string): void {
  logError(error, ErrorLevel.ERROR, {
    type: 'network',
    endpoint,
  });
}

/**
 * Log authentication error
 */
export function logAuthError(error: unknown, action?: string): void {
  logError(error, ErrorLevel.WARNING, {
    type: 'authentication',
    action,
  });
}

/**
 * Log payment error
 */
export function logPaymentError(error: unknown, paymentId?: string): void {
  logError(error, ErrorLevel.CRITICAL, {
    type: 'payment',
    paymentId,
  });
}

/**
 * Log validation error
 */
export function logValidationError(error: unknown, formName?: string): string {
  return logError(error, ErrorLevel.INFO, {
    type: 'validation',
    formName,
  });
}

/**
 * Recent error entry for storage
 */
interface RecentErrorEntry {
  errorId: string;
  timestamp: string;
  level: ErrorLevel;
  message: string;
  code?: ErrorCode;
}

/**
 * Get recent errors from session storage
 */
export function getRecentErrors(): RecentErrorEntry[] {
  try {
    const stored = sessionStorage.getItem('plz-error-log');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    // Ignore parse errors
  }
  return [];
}

/**
 * Clear error log
 */
export function clearErrorLog(): void {
  try {
    sessionStorage.removeItem('plz-error-log');
  } catch (e) {
    // Ignore storage errors
  }
}

/**
 * Get error log summary for support
 */
export function getErrorLogSummary(): string {
  const errors = getRecentErrors();
  if (errors.length === 0) {
    return 'No recent errors';
  }

  return errors
    .map((err) => `[${err.errorId}] ${err.timestamp}: ${err.message}`)
    .join('\n');
}
